import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  TEST_OVERVIEW_SIDEPANEL_EXECUTION_HISTORY,
  TEST_OVERVIEW_SIDEPANEL_EXECUTION,
} from '@elementary/common-ui/constants/queryKeys';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import {
  TestOverviewExecution,
  TestOverviewExecutionHistory,
} from 'shared/types/execution';
import { TestOverview } from '../types';

type ExecutionHistoryQuery = { test: TestOverviewExecutionHistory } | undefined;

type ExecutionQuery =
  | {
      testExecution: TestOverviewExecution;
    }
  | undefined;

type Props = {
  data?: TestOverview;
};

const usePanelTracking = ({ data }: Props) => {
  const [trackingDataLoaded, setTrackingDataLoaded] = useState<
    boolean | undefined
  >();
  const queryClient = useQueryClient();
  const { selectedId } = useSidePanelParams();

  const updateTrackingData = () => {
    const executionHistoryData: ExecutionHistoryQuery =
      queryClient.getQueryData(
        [TEST_OVERVIEW_SIDEPANEL_EXECUTION_HISTORY, selectedId],
        { exact: false },
      );

    const firstExecutionId =
      executionHistoryData?.test?.executionHistory?.[0]?.id;

    if (firstExecutionId) {
      const firstExecutionData: ExecutionQuery = queryClient.getQueryData(
        [TEST_OVERVIEW_SIDEPANEL_EXECUTION, firstExecutionId],
        { exact: false },
      );

      if (executionHistoryData && firstExecutionData) {
        setTrackingDataLoaded(true);
      }
    } else if (executionHistoryData) {
      setTrackingDataLoaded(true);
    }
  };

  useEffect(() => {
    const unsubscribe = queryClient
      .getQueryCache()
      .subscribe(updateTrackingData);

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  useViewLoadTracking({
    name: 'test overview panel',
    loadDeps: [data, trackingDataLoaded],
    contextId: selectedId || undefined,
  });
};

export default usePanelTracking;
